/**
 * DE RELEASE DIE DEZE BUNDEL DRAAGT.
 *
 * Alles hier komt uit `__COLLECTR_RELEASE__`, één JSON-tekst die Vite bij het
 * bouwen in de bundel bakt (`scripts/release/buildIdentiteit.ts`). De browser
 * berekent niets: hij leest terug wat er bij het bouwen is vastgesteld.
 *
 * WAAROM DE DATUM VAN DE BEZOEKER HIER GEEN ROL SPEELT. Een versienummer dat uit
 * de actuele datum volgt, verandert terwijl het artefact stilstaat: dezelfde
 * bundel zou in september `26.9.x` heten en in oktober `26.10.x`. Het nummer
 * hoort bij het artefact, niet bij het moment van kijken. De datum wordt hier
 * alleen nog GETOOND — het ingebakken bouwtijdstip, opgemaakt in Brusselse tijd.
 *
 * ONTBREEKT DE CONSTANTE, dan is dit geen gebouwde bundel (een test- of
 * ontwikkelruntime). Er wordt dan `null` geleverd en de aanroeper valt terug op
 * de buildaanduiding. Er wordt nooit een versienummer verzonnen.
 */

/** Door Vite ingebakken; bestaat NIET in een test- of ontwikkelruntime. */
declare const __COLLECTR_RELEASE__: string | undefined;

export type Releasekanaal = "live" | "dev";

export type DraaiendeRelease = {
  /**
   * Het Collectr-releasenummer zoals de bundel het meedraagt.
   * Nieuw: `v1.JJ.M.N`. Ouder: `JJ.M.N`. De badge maakt er altijd de publieke vorm van.
   */
  readonly versie: string;
  /** Waaruit deze bundel gebouwd is: de releasebranch, of ontwikkeling. */
  readonly kanaal: Releasekanaal;
  /** True zolang de eerste release nog niet live staat. */
  readonly eersteReleaseNogNietLive: boolean;
  /** Identiteit van dit artefact — hetzelfde als in `build-meta.json`. */
  readonly buildId: string | null;
  readonly commit: string | null;
  /** ISO-tijdstip van bouwen. */
  readonly gebouwdOp: string | null;
  /**
   * Nummer van deze frontendbuild, of null.
   * Dit is niet een worker- of databasenummer.
   */
  readonly frontendBuildnummer: number | null;
  /** Pages-deployment, acht hex-tekens, of null. */
  readonly deploymentId: string | null;
};

function leesRuw(): unknown {
  if (typeof __COLLECTR_RELEASE__ !== "string") return null;
  try {
    return JSON.parse(__COLLECTR_RELEASE__);
  } catch {
    return null;
  }
}

/** Zuivere ontleding, injecteerbaar zodat de vorm toetsbaar is zonder build. */
export function ontleedRelease(ruw: unknown): DraaiendeRelease | null {
  if (typeof ruw !== "object" || ruw === null) return null;
  const o = ruw as Record<string, unknown>;
  const versie = typeof o.versie === "string" ? o.versie.trim() : "";
  const kanaal = o.kanaal === "live" || o.kanaal === "dev" ? o.kanaal : null;
  // Zonder versie of zonder kanaal is er niets zinnigs te tonen. Half invullen
  // zou een nummer opleveren waarvan niemand weet of het live of dev betreft.
  if (versie.length === 0 || kanaal === null) return null;
  const tekstOfNull = (w: unknown): string | null =>
    typeof w === "string" && w.trim().length > 0 ? w.trim() : null;
  return {
    versie,
    kanaal,
    eersteReleaseNogNietLive: o.eersteReleaseNogNietLive === true,
    buildId: tekstOfNull(o.buildId),
    commit: tekstOfNull(o.commit),
    gebouwdOp: tekstOfNull(o.gebouwdOp),
    frontendBuildnummer: leesFrontendBuildnummer(o.frontendBuildnummer),
    deploymentId: tekstOfNull(o.deploymentId),
  };
}

/** Alleen een positief geheel getal. Al het andere telt als afwezig. */
function leesFrontendBuildnummer(w: unknown): number | null {
  if (typeof w !== "number" || !Number.isSafeInteger(w) || w <= 0) return null;
  return w;
}

export function draaiendeRelease(): DraaiendeRelease | null {
  return ontleedRelease(leesRuw());
}

/** Commits zijn lang; zeven tekens volstaat om er één te herkennen. */
export function korteCommit(commit: string | null): string | null {
  const c = commit?.trim() ?? "";
  return c.length > 0 ? c.slice(0, 7) : null;
}

/**
 * Het ingebakken bouwtijdstip, leesbaar in Brusselse tijd. Onleesbare of
 * ontbrekende invoer levert null — dan blijft de regel gewoon weg.
 */
export function formatteerBouwtijdstip(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    /*
     * De delen worden zelf samengevoegd in plaats van een landinstelling de
     * opmaak te laten kiezen. Dezelfde `nl-BE` levert namelijk niet overal
     * dezelfde tekst: Node met volledige ICU schrijft "19/09/2026, 20:30",
     * een andere runtime kan er iets anders van maken. Een badge waarvan de
     * vorm per browser verschilt is niet na te meten.
     */
    const delen = new Intl.DateTimeFormat("nl-BE", {
      timeZone: "Europe/Brussels",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(d);
    const deel = (soort: string): string => delen.find((x) => x.type === soort)?.value ?? "";
    const dag = deel("day");
    const maand = deel("month");
    const jaar = deel("year");
    const uur = deel("hour");
    const minuut = deel("minute");
    if ([dag, maand, jaar, uur, minuut].some((w) => w.length === 0)) return null;
    return `${dag}-${maand}-${jaar} ${uur}:${minuut}`;
  } catch {
    return null;
  }
}
